/**
 * Open Belot 1×1: declarations using hand + own table columns (visible pool).
 * Phase 1: Bella (trump K+Q) after the first completed trick.
 * Phase 2: terz / fifty / hundred / carré + compareTeams vs opponent pool.
 *
 * @module game/belot/openDuell/openDuellDeclarations
 */

const { TRUMP_ALL_TRUMP } = require("../armenian/auction");
const { makeCard, RANK_K, RANK_Q } = require("../armenian/types");
const {
  BELLA_POINTS,
  detectCombinationsFromPool,
  sumComboPoints,
  compareTeams,
} = require("../armenian/declarations");
const { teamOfSeat } = require("./types");

/**
 * All card ids currently held in hand and on table (bottom + top per column).
 *
 * @param {number[]|null|undefined} hand
 * @param {number[][]|null|undefined} tableStacks — 5 columns, bottom..top
 * @returns {number[]}
 */
function collectOpenDuellCardPool(hand, tableStacks) {
  /** @type {number[]} */
  const out = [];
  if (Array.isArray(hand)) {
    for (const c of hand) {
      if (Number.isInteger(c) && c >= 0 && c <= 31) out.push(c);
    }
  }
  if (Array.isArray(tableStacks)) {
    for (const stack of tableStacks) {
      if (!Array.isArray(stack)) continue;
      for (const c of stack) {
        if (Number.isInteger(c) && c >= 0 && c <= 31) out.push(c);
      }
    }
  }
  return out;
}

/**
 * @param {number[]} pool
 * @param {number|null|typeof TRUMP_ALL_TRUMP} trumpSuit — contract.trumpSuit
 * @returns {boolean}
 */
function poolHasBella(pool, trumpSuit) {
  if (trumpSuit == null) return false;
  const set = new Set(pool);
  if (trumpSuit === TRUMP_ALL_TRUMP) {
    for (let s = 0; s <= 3; s += 1) {
      if (set.has(makeCard(s, RANK_K)) && set.has(makeCard(s, RANK_Q))) return true;
    }
    return false;
  }
  if (typeof trumpSuit !== "number" || trumpSuit < 0 || trumpSuit > 3) return false;
  return set.has(makeCard(trumpSuit, RANK_K)) && set.has(makeCard(trumpSuit, RANK_Q));
}

/**
 * After trick 1 completes: each team that holds trump K+Q in hand+own table gets BELLA_POINTS.
 * Runs once per round (caller must set `openDuellFirstTrickDeclarationsDone`).
 *
 * @param {any} state — open duell engine state in `play`
 * @returns {{ delta: [number, number], teamsWithBella: [boolean, boolean] }}
 */
function resolveFirstTrickBellaOnly(state) {
  /** @type {[number, number]} */
  const delta = [0, 0];
  /** @type {[boolean, boolean]} */
  const teamsWithBella = [false, false];
  const c = state?.contract;
  if (!c || c.trumpSuit === undefined) return { delta, teamsWithBella };
  const trump = c.trumpSuit;

  for (let physical = 0; physical <= 1; physical += 1) {
    const hand = state.hands?.[physical];
    const tabs = state.tableStacks?.[physical];
    const pool = collectOpenDuellCardPool(hand, tabs);
    if (!poolHasBella(pool, trump)) continue;
    const tm = teamOfSeat(physical);
    teamsWithBella[tm] = true;
    delta[tm] += BELLA_POINTS;
  }
  return { delta, teamsWithBella };
}

/**
 * Комбо по пулам мест 0 и 1; сравнение команд как в 4p casual.
 *
 * @param {any} state
 * @returns {{
 *   declarationPointsByTeam: [number, number],
 *   declarationGrossByTeam: [number, number],
 *   compare: ReturnType<typeof compareTeams>
 * }}
 */
function resolveFirstTrickComboDeclarations(state) {
  const h0 = state?.hands?.[0];
  const h1 = state?.hands?.[1];
  const t0 = state?.tableStacks?.[0];
  const t1 = state?.tableStacks?.[1];
  const pool0 = collectOpenDuellCardPool(h0, t0);
  const pool1 = collectOpenDuellCardPool(h1, t1);

  const aCombos = detectCombinationsFromPool(pool0);
  const bCombos = detectCombinationsFromPool(pool1);
  const grossA = sumComboPoints(aCombos);
  const grossB = sumComboPoints(bCombos);
  const ctr = state?.contract?.trumpSuit;
  const res = compareTeams(aCombos, bCombos, ctr);
  const scoringA = sumComboPoints(res.scoringA);
  const scoringB = sumComboPoints(res.scoringB);

  return {
    declarationPointsByTeam: /** @type {[number, number]} */ ([scoringA, scoringB]),
    declarationGrossByTeam: /** @type {[number, number]} */ ([grossA, grossB]),
    compare: res,
  };
}

/**
 * Полный резолв после 1-й взятки: Белла + комбо (очки в state как в армянском casual).
 *
 * @param {any} state
 * @returns {{
 *   bellaBonusByTeam: [number, number],
 *   declarationPointsByTeam: [number, number],
 *   declarationGrossByTeam: [number, number],
 *   bella: ReturnType<typeof resolveFirstTrickBellaOnly>,
 *   combos: ReturnType<typeof resolveFirstTrickComboDeclarations>,
 * }}
 */
function resolveFirstTrickOpenDeclarations(state) {
  const prevBella = /** @type {[number, number]} */ ([
    Number(state?.bellaBonusByTeam?.[0]) || 0,
    Number(state?.bellaBonusByTeam?.[1]) || 0,
  ]);
  const bella = resolveFirstTrickBellaOnly(state);
  const bellaBonusByTeam = /** @type {[number, number]} */ ([
    prevBella[0] + bella.delta[0],
    prevBella[1] + bella.delta[1],
  ]);

  const combos = resolveFirstTrickComboDeclarations(state);

  return {
    bellaBonusByTeam,
    declarationPointsByTeam: combos.declarationPointsByTeam,
    declarationGrossByTeam: combos.declarationGrossByTeam,
    bella,
    combos,
  };
}

module.exports = {
  collectOpenDuellCardPool,
  poolHasBella,
  resolveFirstTrickBellaOnly,
  resolveFirstTrickComboDeclarations,
  resolveFirstTrickOpenDeclarations,
  BELLA_POINTS,
};
